export type BulkDeleteOutcome = "success" | "partial" | "failure";

type BulkDeleteCounts = {
    deletedCount: number;
    failedIds: string[];
};

export type BulkDeleteSummary =
    | (BulkDeleteCounts & { outcome: "success"; firstFailure?: undefined })
    /** Rejections are normalized to an Error so Sentry never receives a bare string or object. */
    | (BulkDeleteCounts & { outcome: "partial" | "failure"; firstFailure: Error });

/**
 * Maps the settled results of a per-id bulk delete back onto the requested ids, so a partial failure can
 * be reported without losing track of which deletes went through.
 */
export const summarizeBulkDelete = (
    ids: readonly string[],
    results: readonly PromiseSettledResult<unknown>[],
): BulkDeleteSummary => {
    const failedIds: string[] = [];
    let firstFailure: Error | undefined;

    results.forEach((result, index) => {
        if (result.status !== "rejected") {
            return;
        }
        firstFailure ??= result.reason instanceof Error ? result.reason : new Error(String(result.reason));
        failedIds.push(ids[index]);
    });

    const deletedCount = results.length - failedIds.length;

    if (!firstFailure) {
        return { outcome: "success", deletedCount, failedIds };
    }

    return {
        outcome: failedIds.length === results.length ? "failure" : "partial",
        deletedCount,
        failedIds,
        firstFailure,
    };
};
